import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { IFile } from './files.interface';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl1-files';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IFile, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class FilesCoreService {
  private _loading = false;

  /**
   * @name filesList$
   * @description emits stored list of the files
   */

  filesList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public get loading(): boolean {
    return this._loading;
  }

  public getFiles(params?: IListParams): void {
    this._loading = true;
    this._api.get('file', params, {}).subscribe((data: IFileListResponse) => {
      this.totalCount = data.totalCount;
      store.update(
        setEntities(data.list),
        setProp('lastRefreshed', new Date()),
      );
      this._loading = false;
    });
  }

  public addFile(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('file', dataInput || {}).pipe(
      tap((data: IFile) => {
        store.update(addEntities(data), setProp('lastRefreshed', new Date()));
        this._loading = false;
      }),
    );
  }

  public editFile(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('file', dataInput || {}).pipe(
      map(() => {
        store.update(
          updateEntities(dataInput['_id'], dataInput),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public deleteFile(data): Observable<any> {
    this._loading = true;
    return this._api.delete('file', { body: data }).pipe(
      map(() => {
        store.update(
          deleteEntities(data['_id']),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      }),
    );
  }

  public getFileInfo(id: string): IFile {
    const file = store.query(getEntity(id));
    return file;
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  public resolveFileId(file: string | IFile): string {
    let fileId: string | IFile = '';
    if (file) {
      fileId = file;
    }
    if (file['_id']) {
      fileId = file['_id'];
    }
    return fileId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getFiles();
    }
  }
}

interface IFileListResponse {
  list: IFile[];
  totalCount: number;
}
